import { Image, Pressable, RefreshControl, Text, View, FlatList, ActivityIndicator, Modal, Platform, ScrollView, useWindowDimensions } from 'react-native';
import React, { useCallback, useRef, useState, useMemo } from 'react';
import DefaultScreen from '@components/Global/DefaultScreenView';
import BottomSheet, { BottomSheetModal, BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { useNavigation } from '@react-navigation/native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apis, { duplicateChildTemplate, duplicateEmailTemplate, generateEmailTemplateWithAI } from '@api/api';
import styles from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import AchievementBackground from '@assets/--native/AchievementBackground';
import moment from 'moment';
import parseByDelimiter from '@utils/parseByDelimiter';
import { Title } from "@constants/Themed";
import MaskedView from '@react-native-masked-view/masked-view';
import { Entypo, Ionicons, AntDesign, FontAwesome5, FontAwesome6 } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import DeleteConfirmModal from '@components/modals/DeleteConfirmModal/DeleteConfirmModal';
import { StyledModal, StyledModalHandle } from '@constants/Themed';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import PreviewModal, { PreviewModalHandler } from './components/PreviewModal';
import GetDefaultEmailTemplates from './components/GetDefaultEmailTemplates';
import CompanyEmailItem from './components/CompanyEmailItem';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { AlertDialog, Button, Input, Label, Popover, Select, Separator, SizableText, TextArea, XStack, YStack } from 'tamagui';
import Svg, { Path, Rect } from 'react-native-svg';
import CountUp from 'react-countup';
import ErrorBoundary from '@utils/ErrorBoundary';
import { useDeviceType } from '@hooks/useDeviceType';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { Tooltip } from '@mui/material';
import LottieView from 'lottie-react-native';

interface EmailTemplateTypes {
  _id: string;
  templateTitle: string;
  templateDescription: string;
  templateContent: string;
  templateTags: string[];
  status?: string;
  title?: string;
  type?: string;
  global?: boolean;
  createdAt?: string;
  createdBy?: any;
  description?: string;
  snapShot?: string;
  parent?: string;
}

// Modern container styles that work with Redux theme
const getModernContainerStyle = (isDarkTheme: boolean): any => {
  const baseStyle: any = {
    borderRadius: 20,
    padding: 24,
    marginVertical: 16,
    backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: isDarkTheme ? 0.4 : 0.12,
    shadowRadius: 32,
    elevation: isDarkTheme ? 10 : 6,
    borderWidth: 1,
    borderColor: isDarkTheme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
    overflow: 'hidden',
    position: 'relative',
  };

  if (Platform.OS === 'web') {
    baseStyle.marginHorizontal = 20;
  } else {
    baseStyle.marginHorizontal = 10;
  }

  return baseStyle;
};

const getSectionHeaderStyle = (isDarkTheme: boolean) => ({
  fontSize: 20,
  fontWeight: '700' as const,
  color: isDarkTheme ? '#f1f5f9' : '#1e293b',
  marginBottom: 16,
  borderBottomWidth: 1,
  borderBottomColor: isDarkTheme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
  paddingBottom: 12,
});

const LoadingContainer = ({ children }: { children: React.ReactNode }) => (
  <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 60, minHeight: 200 }}>
    {children}
  </View>
);

const getStatsContainerStyle = (theme: boolean) => ({
  backgroundColor: theme ? 'rgba(102, 126, 234, 0.1)' : 'rgba(74, 144, 226, 0.1)',
  borderRadius: 12,
  padding: 16,
  marginVertical: 8,
});

const AnimatedSection = ({ children, ...props }: any) => (
  <Animatable.View style={{ marginVertical: 8 }} {...props}>
    {children}
  </Animatable.View>
);

const EmailTemplates = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const emailTemplateRef = useRef<BottomSheet>(null);
  const navigation = useNavigation();
  const [refreshing, setRefreshing] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [activeItem, setActiveItem] = useState<EmailTemplateTypes>({ _id: '', templateTitle: '', templateDescription: '', templateContent: '', templateTags: [] });
  const { isTablet } = useDeviceType();
  const tablet = isTablet;
  const addEmailTemplateRef = useRef<StyledModalHandle>(null);
  const [previewHTML, setPreviewHTML] = useState('');
  const previewRef = useRef<PreviewModalHandler>(null);
  const isCompany = true;
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [myTemplatesOpen, setMyTemplatesOpen] = useState(false);
  const theme = themeSelector();
  const childTitleRef = useRef<Input>(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);
  const currentUser = useSelector((state: DefaultRootState) => state.promiseData.data);
  const { width } = useWindowDimensions();
  const queryClient = useQueryClient();
  const handleModalState = useCallback((item) => {
    setActiveItem(item);
    setDeleteModalVisible(true);
  }, [activeItem])

  const handleHideDeleteModal = useCallback(() => {
    setDeleteModalVisible(false);
  }, [deleteModalVisible])

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch()
      .then(() =>
        setRefreshing(false));
  }, []);

  const handleShowModal = useCallback(() => {
    console.log('opening modal')
    emailTemplateRef.current?.expand();
  }, [emailTemplateRef]);

  const { status, data: emailTemplates, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['emailTemplates'],
    queryFn: async () => {
      const data = await apis.getEmailTemplates()
        .then(res => {
          return res || []
        });
      return data.reverse()
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 2,
  })

  // Filter out children for the main list
  const rootTemplates = useMemo(() => {
    return emailTemplates?.filter(item => !item.parent) || [];
  }, [emailTemplates]);

  const duplicateTemplate = async (item: { _id: any; }, asChild: boolean, title?: string,) => {
    if (asChild) {
      await duplicateChildTemplate(item._id, title).then(() => { refetch() })
    } else {
      await duplicateEmailTemplate(item._id).then(() => { refetch() })
    }
  }

  function toCapitalCase(name: string) {
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  }

  const RenderItem = ({ item, hideDuplicate, index, level = 0 }) => {
    // Find children for this item
    const children = emailTemplates?.filter(child => child.parent === item._id) || [];

    return item && (
      <ErrorBoundary>
        <Animatable.View
          animation="fadeInUp"
          duration={600}
          delay={index * 100}
          style={{ marginBottom: 16 }}
        >
          {/* Card Container */}
          <LinearGradient
            colors={theme
              ? ['rgba(255,255,255,0.95)', 'rgba(248,250,252,0.95)']
              : ['rgba(45,45,45,0.95)', 'rgba(30,41,59,0.95)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              borderRadius: 20,
              padding: 2,
              shadowColor: theme ? '#000000' : '#ffffff',
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: theme ? 0.1 : 0.3,
              shadowRadius: 12,
              elevation: 8,
            }}
          >
            <View style={{
              backgroundColor: 'transparent',
              borderRadius: 18,
              overflow: 'hidden',
              padding: 24,
              flexDirection: 'row',
              gap: 20,
              alignItems: 'stretch'
            }}>
              {/* Background Decor */}
              <AchievementBackground
                green={item?.type === 'project'}
                style={{
                  position: 'absolute',
                  overflow: 'hidden',
                  width: '200%',
                  height: '200%',
                  top: -50,
                  left: -50,
                  opacity: 0.7,
                  zIndex: -1,
                  transform: [{ scale: tablet ? 20 : 10 }]
                }}
              />

              <MaskedView
                style={{ zIndex: -1, position: 'absolute', right: -20, bottom: -20, height: '100%', width: '100%', pointerEvents: 'none' }}
                maskElement={
                  <View style={{ flex: 1, opacity: 0.2, position: 'absolute', bottom: -40, right: -20 }}>
                    <Entypo name="email" size={140} color="white" />
                  </View>
                }
              >
                <LinearGradient colors={['#ececec80', '#ececec20']} start={{ x: 0.5, y: 0.9 }} style={{ flex: 1, opacity: 0.8 }} />
              </MaskedView>

              {/* Preview on the Left */}
              {Platform.OS === 'web' && item.snapShot && (
                <Pressable
                  onPress={() => { previewRef.current?.openModal?.(item) }}
                  style={{
                    minWidth: 200,
                    width: 200,
                    minHeight: 300,
                    borderRadius: 12,
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.3,
                    shadowRadius: 8,
                    elevation: 5
                  }}
                >
                  <Image
                    source={{ uri: item.snapShot }}
                    style={{
                      width: '100%',
                      height: '100%',
                      resizeMode: 'cover'
                    }}
                  />
                </Pressable>
              )}

              {/* Content on the Right */}
              <View style={{ flex: 1, gap: 16 }}>
                {/* Header Row: Title & Actions */}
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap-reverse' }}>
                  <View style={{ flex: 1, minWidth: 200 }}>
                    <Text style={{ color: 'white', fontWeight: 'bold', fontSize: 18, marginBottom: 4 }}>
                      {parseByDelimiter(item?.title)}
                    </Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Text style={{
                        backgroundColor: item.status === 'active' ? "green" : "grey",
                        paddingHorizontal: 8,
                        paddingVertical: 2,
                        borderRadius: 4,
                        color: "white",
                        fontSize: 12,
                        fontWeight: "600",
                        overflow: 'hidden'
                      }}>
                        {toCapitalCase(item.status) || "DRAFT"}
                      </Text>
                      <Text style={{ color: "white", fontSize: 12 }}>
                        {moment(item.createdAt).format(globalDate)}
                      </Text>
                    </View>
                  </View>

                  {/* Actions Toolbar */}
                  <XStack gap={10} alignItems="center" backgroundColor="rgba(0,0,0,0.2)" padding={8} borderRadius={12}>
                    <Pressable onPress={async () => { await apis.setEmailGlobal(item._id) }}>
                      <FontAwesome5 name="globe-americas" size={18} color={item?.global ? "rgb(90,192,234)" : "white"} />
                    </Pressable>

                    <Pressable>
                      <AntDesign name="edit" size={18} color="white" />
                    </Pressable>

                    <Pressable>
                      <AntDesign name="share-alt" size={18} color="white" />
                    </Pressable>

                    {!hideDuplicate && (
                      <Popover allowFlip stayInFrame>
                        <Popover.Trigger>
                          <FontAwesome6 name="copy" size={18} color="white" />
                        </Popover.Trigger>
                        <Popover.Content borderWidth={0} unstyled elevate zIndex={100}
                          style={{ borderRadius: 20, backgroundColor: "#ececec", padding: 10, minWidth: 200 }}
                        >
                          <YStack gap={10}>
                            {/* Duplicate as Child Dialog */}
                            <AlertDialog native>
                              <AlertDialog.Trigger asChild>
                                <Pressable style={{ padding: 10 }}>
                                  <Text style={{ color: 'black', fontWeight: 'bold' }}>Duplicate as child</Text>
                                  <Text style={{ color: '#666', fontSize: 12 }}>Create variant</Text>
                                </Pressable>
                              </AlertDialog.Trigger>
                              <AlertDialog.Portal>
                                <AlertDialog.Overlay opacity={0.5} style={{ zIndex: 1000 }} />
                                <AlertDialog.Content bordered elevate style={{ padding: 20, borderRadius: 16, minWidth: 300, gap: 16 }}>
                                  <AlertDialog.Title>Duplicate Template</AlertDialog.Title>
                                  <Input ref={childTitleRef} placeholder="Child template name" />
                                  <XStack gap={10} justifyContent="flex-end">
                                    <AlertDialog.Cancel asChild><Button>Cancel</Button></AlertDialog.Cancel>
                                    <AlertDialog.Action asChild>
                                      <Button onPress={async () => {
                                        const input = childTitleRef.current;
                                        const val = input && 'value' in input ? (input as any).value : '';
                                        await duplicateTemplate(item, true, val);
                                      }} theme="active">Duplicate</Button>
                                    </AlertDialog.Action>
                                  </XStack>
                                </AlertDialog.Content>
                              </AlertDialog.Portal>
                            </AlertDialog>
                            <Separator />
                            <Pressable onPress={() => duplicateTemplate(item, false)} style={{ padding: 10 }}>
                              <Text style={{ color: 'black', fontWeight: 'bold' }}>Duplicate new</Text>
                              <Text style={{ color: '#666', fontSize: 12 }}>Create independent copy</Text>
                            </Pressable>
                          </YStack>
                        </Popover.Content>
                      </Popover>
                    )}

                    <Pressable onPress={() => handleModalState(item)}>
                      <AntDesign name="delete" size={18} color="#ff6b6b" />
                    </Pressable>
                  </XStack>
                </View>

                {/* Description */}
                <Text style={{ color: 'white', fontStyle: "italic", opacity: 0.9 }}>{item.description}</Text>

                {/* Footer: Author & Nav */}
                <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 'auto' }}>
                  {item.createdBy && (
                    <View style={{ flexDirection: "row", alignItems: 'center', gap: 10 }}>
                      <AvatarHandler
                        avatar={item.createdBy.profile_information?.avatar?.img}
                        avatarImageStyle={{ width: 30, height: 30, borderRadius: 15 }}
                        replacementImageStyle={{ maxWidth: 30, maxHeight: 30 }}
                      />
                      <View>
                        <Text style={{ color: 'white', fontSize: 10, fontWeight: 'bold' }}>Created by:</Text>
                        <Text style={{ color: 'white', fontSize: 12 }}>
                          {toCapitalCase(item.createdBy.profile_information?.name) + " " + toCapitalCase(item.createdBy.profile_information?.surname)}
                        </Text>
                      </View>
                    </View>
                  )}

                  <Pressable
                    onPress={() => (navigation as any).navigate('PreviewEmailTemplate', { templateId: item._id })}
                    style={{ width: 40, height: 40, backgroundColor: "rgb(90,192,234)", borderRadius: 20, alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Ionicons name="chevron-forward" size={24} color="white" style={{ marginLeft: 2 }} />
                  </Pressable>
                </View>
              </View>
            </View>
          </LinearGradient>

          {/* Render Children Recursively */}
          {children.length > 0 && (
            <View style={{ paddingLeft: 20, borderLeftWidth: 2, borderLeftColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)', marginLeft: 10 }}>
              {children.map((child, idx) => (
                <RenderItem key={child._id} item={child} index={idx} hideDuplicate={true} level={level + 1} />
              ))}
            </View>
          )}
        </Animatable.View>
      </ErrorBoundary>
    )
  }

  const _uploadBulkMedia = async () => { }

  const _handleAddEmailTemplate = async (values) => {
    const data = {
      ...values,
      content: values.content?.template || values.content || "",
    }
    await apis.saveEmailTemplate(data)
      .then(() => {
        onRefresh();
        addEmailTemplateRef.current?.closeModal();
      });
  }

  const addEmailSchema = {
    title: { validation: { required: true }, helpText: "The title of the email template" },
    description: { validation: { required: true }, helpText: "The description of the email template" },
    tags: { ref: 'tags', helpText: "Add some tags to help categorize your email template" },
    content: {},
    expires: { type: 'date', helpText: "Set an expiry date for the email template" }
  }

  const ListHeader = () => (
    <View style={{ marginBottom: 20 }}>
      {/* Loading State */}
      {isLoading && (
        <LoadingContainer>
          {Platform.OS === 'web' ? (
            <div style={{ width: 60, height: 60, marginBottom: 16 }}>
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          ) : (
            <LottieView
              source={require('@assets/animations/loading.json')}
              autoPlay
              loop
              style={{ width: 60, height: 60, marginBottom: 16 }}
            />
          )}
          <Text style={{ color: theme ? '#666666' : '#cccccc', fontSize: 16, textAlign: 'center' }}>
            Loading templates...
          </Text>
        </LoadingContainer>
      )}

      {/* Default Header & Footer Section */}
      {!isLoading && (
        <AnimatedSection animation="fadeInUp" duration={800}>
          <View style={{ marginBottom: 20 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <View style={{ backgroundColor: theme ? 'rgba(102, 126, 234, 0.1)' : 'rgba(74, 144, 226, 0.1)', borderRadius: 10, padding: 10, marginRight: 12 }}>
                  <Text style={{ fontSize: 18 }}>📧</Text>
                </View>
                <Text style={{ ...getSectionHeaderStyle(!theme), borderBottomWidth: 0, paddingBottom: 0, marginBottom: 0 }}>
                  Default Header & Footer
                </Text>
              </View>
              <Pressable onPress={() => setTemplatesOpen(!templatesOpen)} style={{ padding: 8 }}>
                <AntDesign name={!templatesOpen ? "caret-down" : "caret-up"} size={20} color={theme ? '#667eea' : '#4a90e2'} />
              </Pressable>
            </View>

            {templatesOpen && (
              <Animatable.View animation="fadeInDown" duration={600} style={{ marginTop: 20 }}>
                <XStack flexWrap='wrap' gap={12}>
                  <View style={{ ...getStatsContainerStyle(theme), flex: 1, minWidth: 150 }}>
                    <View style={{ flexDirection: "row", justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text style={{ color: theme ? '#333' : '#fff', fontWeight: '600' }}>Header Templates</Text>
                      <Pressable onPress={() => addEmailTemplateRef.current?.openModal?.()} style={{ backgroundColor: theme ? '#667eea' : '#4a90e2', padding: 6, borderRadius: 20 }}>
                        <AntDesign name="plus" size={14} color="white" />
                      </Pressable>
                    </View>
                  </View>
                  <View style={{ ...getStatsContainerStyle(theme), flex: 1, minWidth: 150 }}>
                    <View style={{ flexDirection: "row", justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text style={{ color: theme ? '#333' : '#fff', fontWeight: '600' }}>Footer Templates</Text>
                      <Pressable onPress={() => addEmailTemplateRef.current?.openModal?.()} style={{ backgroundColor: theme ? '#667eea' : '#4a90e2', padding: 6, borderRadius: 20 }}>
                        <AntDesign name="plus" size={14} color="white" />
                      </Pressable>
                    </View>
                  </View>
                </XStack>
              </Animatable.View>
            )}
          </View>
        </AnimatedSection>
      )}

      {/* Company Templates Section */}
      {!isLoading && isCompany && (
        <AnimatedSection animation="fadeInUp" duration={800} delay={200} style={{ marginVertical: 10 }}>
          <View style={{ ...getModernContainerStyle(!theme), padding: 20 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
              <FontAwesome5 name="globe-americas" size={18} color={theme ? '#667eea' : '#4a90e2'} style={{ marginRight: 10 }} />
              <Text style={{ ...getSectionHeaderStyle(!theme), borderBottomWidth: 0, paddingBottom: 0, marginBottom: 0 }}>Company Templates</Text>
            </View>
            <FlatList
              data={emailTemplates?.filter(item => item?.global) || []}
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ gap: 60 }}
              renderItem={({ item, index }) => <CompanyEmailItem ref={previewRef} item={item} index={index} />}
              ListEmptyComponent={
                <View style={{ padding: 20, alignItems: 'center' }}>
                  <Text style={{ color: theme ? '#666' : '#ccc' }}>No global templates yet.</Text>
                </View>
                }
            />
          </View>
        </AnimatedSection>
      )}

      {/* Your Templates Header */}
      {!isLoading && (
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 20, marginBottom: 10 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <Entypo name="email" size={20} color={theme ? '#667eea' : '#4a90e2'} />
            <Text style={{ ...getSectionHeaderStyle(!theme), borderBottomWidth: 0, paddingBottom: 0, marginBottom: 0 }}>Your Templates</Text>
            {Platform.OS === 'web' && (
              <View style={{ backgroundColor: theme ? 'rgba(102, 126, 234, 0.1)' : 'rgba(74, 144, 226, 0.1)', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8 }}>
                <Text style={{ color: theme ? '#667eea' : '#4a90e2', fontWeight: 'bold' }}>
                  <CountUp end={emailTemplates?.length || 0} duration={1} />
                </Text>
              </View>
            )}
          </View>
          <Pressable onPress={() => setMyTemplatesOpen(!myTemplatesOpen)} style={{ padding: 8 }}>
            <AntDesign name={myTemplatesOpen ? "caret-down" : "caret-up"} size={20} color={theme ? '#667eea' : '#4a90e2'} />
          </Pressable>
        </View>
      )}
    </View>
  );

  return (
    <DefaultScreen
      containerStyle={{ zIndex: -1 }}
      scrollDisabled
      style={{ paddingRight: 0 }}
      backButton
      aiSearch
      aiCallback={async (text: string) => {
        try {

          // Generate template using AI
          const response = await generateEmailTemplateWithAI(text);

          if (response?.success && response?.data) {
            const templateData = response.data;

            // Save the generated template
            const saveResponse = await apis.saveEmailTemplate({
              title: templateData.title,
              description: templateData.description,
              content: typeof templateData.content === 'string'
                ? templateData.content
                : templateData.content?.template || templateData.content?.html || '',
              tags: templateData.tags || [],
            });

            // Refresh the list and invalidate queries
            queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
            const { data: updatedTemplates } = await refetch();

            // Navigate to preview with the new template
            if (saveResponse?.success && updatedTemplates) {
              // Find the template by matching title and description
              // Since templates are reversed, check from the beginning (newest first)
              const newTemplate = updatedTemplates.find(
                (t: any) => t.title === templateData.title &&
                  t.description === templateData.description
              );

              if (newTemplate?._id) {
                (navigation as any).navigate('PreviewEmailTemplate', {
                  templateId: newTemplate._id
                });
              } else {
                // If exact match not found, navigate to the first template (newest)
                const newestTemplate = updatedTemplates[0];
                if (newestTemplate?._id) {
                  (navigation as any).navigate('PreviewEmailTemplate', {
                    templateId: newestTemplate._id
                  });
                } else {
                  console.warn('Template saved but could not find ID');
                }
              }
            } else {
              console.warn('Template saved but no success response or data not available');
            }
          } else {
            console.error('Failed to generate template:', response);
          }
        } catch (error) {
          console.error('❌ Error generating email template:', error);
        }
      }}
      searchBar={{
        aiSearch: true,
        aiCallback: async (text: string) => {
          try {
            console.log('📧 Generating email template with AI:', text);

            // Generate template using AI
            const response = await generateEmailTemplateWithAI(text);

            if (response?.success && response?.data) {
              const templateData = response.data;

              // Save the generated template
              const saveResponse = await apis.saveEmailTemplate({
                title: templateData.title,
                description: templateData.description,
                content: typeof templateData.content === 'string'
                  ? templateData.content
                  : templateData.content?.template || templateData.content?.html || '',
                tags: templateData.tags || [],
              });

              // Refresh the list and invalidate queries
              queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
              const { data: updatedTemplates } = await refetch();

              // Navigate to preview with the new template
              if (saveResponse?.success && updatedTemplates) {
                // Find the template by matching title and description
                // Since templates are reversed, check from the beginning (newest first)
                const newTemplate = updatedTemplates.find(
                  (t: any) => t.title === templateData.title &&
                    t.description === templateData.description
                );

                if (newTemplate?._id) {
                  (navigation as any).navigate('PreviewEmailTemplate', {
                    templateId: newTemplate._id
                  });
                } else {
                  // If exact match not found, navigate to the first template (newest)
                  const newestTemplate = updatedTemplates[0];
                  if (newestTemplate?._id) {
                    (navigation as any).navigate('PreviewEmailTemplate', {
                      templateId: newestTemplate._id
                    });
                  } else {
                    console.warn('Template saved but could not find ID');
                  }
                }
              } else {
                console.warn('Template saved but no success response or data not available');
              }
            } else {
              console.error('Failed to generate template:', response);
            }
          } catch (error) {
            console.error('❌ Error generating email template:', error);
          }
        },
        customIcons: [{
          function: () => {
            console.log("opening email template");
            addEmailTemplateRef.current?.openModal?.()
          },
          functionName: 'createEmailTemplate',
          icon: <AntDesign name="file" size={16} color={"white"} />
        }, {
          function: () => _uploadBulkMedia(),
          functionName: 'uploadBulkMedia',
          icon: <AntDesign name="upload" size={16} color={"white"} />
        }]
      }}
    >
      <ErrorBoundary>
        <PreviewModal ref={previewRef} />
        <StyledModal
          onModalWillHide={() => {
            queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
          }}
          title="Add new email template"
          caption="Create a new email template from scratch or use a default template"
          containerStyle={{ padding: "5%" }}
          customContent={(
            <View style={{ flexGrow: 10, alignSelf: "stretch", flex: 1 }}>
              <GetDefaultEmailTemplates callback={(item) => setSelectedTemplate(item)} />
            </View>
          )}
          ref={addEmailTemplateRef}
        >
          <GenerateSchemaForm extraValues={{ content: selectedTemplate?._id }} schema={addEmailSchema} extraCallBack={()=>{queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });}} nullify={['content']} buttonLabel={selectedTemplate ? "Create template" : "Create blank"} submitFunction={_handleAddEmailTemplate} />
        </StyledModal>

        <DeleteConfirmModal itemId={activeItem?._id} deleteFunction={apis.deleteEmailTemplate} title={parseByDelimiter(activeItem?.templateTitle || "")} refreshFunction={onRefresh} setState={handleHideDeleteModal} visibleState={deleteModalVisible} />

        <FlatList
          data={(!isLoading && !myTemplatesOpen) ? rootTemplates : []}
          renderItem={({ item, index }) => <RenderItem item={item} index={index} hideDuplicate={false} />}
          keyExtractor={item => item._id}
          ListHeaderComponent={ListHeader}
          ListEmptyComponent={
            (!isLoading && !myTemplatesOpen) ? (
              <View style={{ ...getModernContainerStyle(!theme), padding: 40, alignItems: 'center', justifyContent: 'center', borderStyle: 'dashed' }}>
                <Animatable.View animation="pulse" iterationCount="infinite" duration={3000}>
                  <Entypo name="email" size={48} color={theme ? 'rgba(102, 126, 234, 0.5)' : 'rgba(74, 144, 226, 0.5)'} />
                </Animatable.View>
                <Text style={{ marginTop: 16, color: theme ? '#94a3b8' : '#64748b', fontSize: 18, fontWeight: '600' }}>
                  Create your first template
                </Text>
                <Text style={{ marginTop: 8, color: theme ? '#64748b' : '#94a3b8', textAlign: 'center' }}>
                  Get started by creating a new email template from scratch or using a default one.
                </Text>
                <Button
                  onPress={() => addEmailTemplateRef.current?.openModal?.()}
                  style={{ marginTop: 20, backgroundColor: theme ? '#667eea' : '#4a90e2', borderRadius: 20 }}
                >
                  <Text style={{ color: 'white', paddingHorizontal: 10 }}>Create Template</Text>
                </Button>
              </View>
            ) : null
          }
          contentContainerStyle={{ padding: 20, paddingBottom: 100 }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
          style={{ flex: 1 }}
        />
      </ErrorBoundary>
    </DefaultScreen>
  )
}
export default EmailTemplates;
